import { useEffect } from 'react';
import {
  CONSUMER_CMS_SHOPPING_CART,
  COOKIE_CART,
  errorMock,
  IGenericErrorCMS,
  IShoppingCartResponse,
  useCmsConfig,
  useDeleteShoppingCartMutation,
} from '@vfit/consumer/data-access';
import {
  API,
  getClickToCallWithKeyOrUrl,
  getFromLocalStorageByKey,
  setItemCookie,
  trackView,
} from '@vfit/shared/data-access';
import {
  errorManager,
  ErrorService,
  ITrackError,
  LoggerInstance,
  toastManager,
} from '@vfit/shared/data-access';
import { useQueryClient } from 'react-query';
import { IUseCheckoutErrors } from './useCheckoutErrors.models';
import { getTrackError } from '../../checkout.utils';
import { useCheckout } from '../../../iBuyFixed.context';
import { ENABLE_DEBUG } from '../../../iBuyFixed.utils';
import { ICMSApiError, ICMSError } from '@vfit/shared/models';

export const useCheckoutErrors = ({
  handleClose,
  replaceBillingAddress,
  createBillingAddress,
}: IUseCheckoutErrors) => {
  const {
    product,
    currentStepKey,
    customerDeliveryBaseInfo,
    customerAsyncInfo,
    customerFlow,
    deliveryAdvance,
    authorizationAsyncInfo,
    checkoutErrors,
    isModalButtonSticky,
    setCheckoutErrors,
  } = useCheckout();
  const queryClient = useQueryClient();
  const {
    mutate: deleteCartMutation,
    isSuccess: isSuccessDeleteCart,
    isLoading: isLoadingDeleteCart,
    isError: isErrorDeleteCart,
  } = useDeleteShoppingCartMutation();
  const genericError = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GENERIC_ERROR
  ) as IGenericErrorCMS;

  const showAndTrackError = (
    _genericError: IGenericErrorCMS,
    _handleClose?: () => void,
    error?: ITrackError,
    title?: string,
    message?: string,
    actionEvent?: () => void,
    actionText?: string,
    secondActionEvent?: () => void,
    secondActionText?: string,
    isButtonSticky = false,
    disableDeleteCart = false,
    disableTrack = false,
    invertButtons = false,
    icon?: string
  ) => {
    const trackErrorRes = error
      ? getTrackError(error, currentStepKey?.toLowerCase(), queryClient)
      : undefined;

    const deleteCartClose = () => {
      _handleClose?.();
      window.location.reload();
    };

    const STATUS_DELETE_CART = ['500', '403', '404'];
    const actualCart: IShoppingCartResponse = getFromLocalStorageByKey('shoppingCart');
    const cartId = actualCart?.id;
    const ENABLE_DELETE_CART =
      (!disableDeleteCart &&
        cartId &&
        error?.error?.status &&
        STATUS_DELETE_CART.includes(error?.error?.status)) ||
      error?.error?.status === 'timeout';
    if (ENABLE_DELETE_CART && !isSuccessDeleteCart && !isLoadingDeleteCart && !isErrorDeleteCart) {
      deleteCartMutation({
        shoppingCartId: cartId,
        customOptions: {
          headers: {
            Authorization: sessionStorage.getItem('uxfauthorization') || '',
          },
        },
      });
    }

    if (trackErrorRes) {
      if (ENABLE_DEBUG) {
        toastManager.handleToast({
          title: trackErrorRes.page_error || 'no error found',
        });
      }
      LoggerInstance.log('Track', trackErrorRes);
      trackView(trackErrorRes);
    }
    errorManager.handleError(ErrorService.getSeverityErrorHigh(), {
      title: title || _genericError?.genericerror?.title || '',
      message: message || _genericError?.genericerror?.description || '',
      onClose: ENABLE_DELETE_CART ? deleteCartClose : _handleClose,
      actionEvent: ENABLE_DELETE_CART ? deleteCartClose : actionEvent || _handleClose,
      actionText: actionText || _genericError?.genericerror?.buttonLabel || '',
      disableTrack: !!trackErrorRes || disableTrack,
      secondActionEvent,
      secondButtonText: secondActionText,
      isButtonSticky,
      invertButtons,
      ...(icon && { icon }),
      ...(trackErrorRes && {
        trackError: {
          ...trackErrorRes,
        },
      }),
      opts: {
        product_name: product?.slug || '',
      },
    });
  };

  const { errorcomponent: errorComponentCustomer } =
    (useCmsConfig(CONSUMER_CMS_SHOPPING_CART, API.CMS_GET_CUSTOMER_ERROR_FIXED) as ICMSApiError) ||
    {};
  const customerErrors = errorComponentCustomer?.errors || [];

  const errors: ICMSError[] = [...customerErrors];

  const getCtcConfig = (foundedError: ICMSError | undefined, error?: ITrackError) => {
    if (foundedError?.isClickToCall) {
      return {
        title: foundedError?.title || genericError?.genericerror?.title,
        message: foundedError?.message || genericError?.genericerror?.description,
        secondActionEvent: () =>
          getClickToCallWithKeyOrUrl(
            queryClient,
            foundedError?.keyClickToCall || product?.genericErrorCallMeNow?.url || '',
            product,
            handleClose,
            currentStepKey,
            error
          ),
        secondActionText: foundedError?.button?.title || product?.genericErrorCallMeNow?.label,
        isButtonSticky: isModalButtonSticky,
      };
    }

    return {
      title: genericError?.genericerror?.title,
      message: genericError?.genericerror?.description,
      secondActionEvent: () => {},
      secondActionText: '',
      isButtonSticky: false,
    };
  };

  // region ERRORS

  useEffect(() => {
    if (customerDeliveryBaseInfo?.deliveryBase.isError) {
      LoggerInstance.log('Error: deliverybase_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...customerDeliveryBaseInfo?.deliveryBase?.error,
        isBlocking: true,
      });
    }
  }, [customerDeliveryBaseInfo?.deliveryBase]);

  useEffect(() => {
    if (customerFlow?.payment?.isError) {
      const getErrorCode = () => customerFlow?.payment?.error?.errorCode?.toString();
      const foundedError = errors?.find((el) => el?.errorCode === getErrorCode());
      const ctcConfig = getCtcConfig(foundedError, customerFlow?.payment?.error);

      LoggerInstance.log('Error: payment_error');
      checkoutErrors?.showAndTrackError?.(
        genericError,
        handleClose,
        {
          ...customerFlow?.payment?.error,
          isBlocking: true,
        },
        ctcConfig?.title,
        ctcConfig?.message,
        undefined,
        '',
        ctcConfig?.secondActionEvent,
        ctcConfig?.secondActionText,
        ctcConfig?.isButtonSticky
      );
    }
  }, [customerFlow?.payment, customerFlow?.payment?.isError]);

  useEffect(() => {
    if (customerFlow?.paymean?.isError) {
      const getErrorCode = () => customerFlow?.paymean?.error?.errorCode?.toString();
      const foundedError = errors?.find((el) => el?.errorCode === getErrorCode());
      const ctcConfig = getCtcConfig(foundedError, customerFlow?.paymean?.error);

      LoggerInstance.log('Error: paymean_error');
      checkoutErrors?.showAndTrackError?.(
        genericError,
        handleClose,
        {
          ...customerFlow?.paymean?.error,
          isBlocking: true,
        },
        ctcConfig?.title,
        ctcConfig?.message,
        undefined,
        '',
        ctcConfig?.secondActionEvent,
        ctcConfig?.secondActionText,
        ctcConfig?.isButtonSticky
      );
    }
  }, [customerFlow?.paymean]);

  useEffect(() => {
    if (customerFlow?.billingAccount.isError) {
      const getErrorCode = () => customerFlow?.billingAccount?.error?.errorCode?.toString();
      const foundedError = errors?.find((el) => el?.errorCode === getErrorCode());
      const ctcConfig = getCtcConfig(foundedError, customerFlow?.billingAccount?.error);

      LoggerInstance.log('Error: billingaccount_error');
      checkoutErrors?.showAndTrackError?.(
        genericError,
        handleClose,
        customerFlow?.billingAccount?.error,
        ctcConfig?.title,
        ctcConfig?.message,
        undefined,
        '',
        ctcConfig?.secondActionEvent,
        ctcConfig?.secondActionText,
        ctcConfig?.isButtonSticky
      );
    }
  }, [customerFlow?.billingAccount]);

  useEffect(() => {
    if (customerAsyncInfo?.retrieveMultilines.isError) {
      LoggerInstance.log('Error: retrieveMultilines_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...customerAsyncInfo?.retrieveMultilines?.error,
        isBlocking: true,
      });
    }
  }, [customerAsyncInfo?.retrieveMultilines]);

  useEffect(() => {
    if (customerAsyncInfo?.termsAndConditions.isError) {
      LoggerInstance.log('Error: terms_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...customerAsyncInfo?.termsAndConditions?.error,
        isBlocking: true,
      });
    }
  }, [customerAsyncInfo?.termsAndConditions]);

  useEffect(() => {
    if (customerFlow?.associateCustomer?.isError) {
      LoggerInstance.log('Error: associateCustomer_error');

      const getErrorCode = () => customerFlow?.associateCustomer?.error?.errorCode?.toString();
      const foundedError = errors?.find((el) => el?.errorCode === getErrorCode());
      const ctcConfig = getCtcConfig(foundedError, customerFlow?.associateCustomer?.error);

      checkoutErrors?.showAndTrackError?.(
        genericError,
        handleClose,
        { ...customerFlow?.associateCustomer?.error, isBlocking: true },
        ctcConfig?.title,
        ctcConfig?.message,
        undefined,
        '',
        ctcConfig?.secondActionEvent,
        ctcConfig?.secondActionText,
        ctcConfig?.isButtonSticky
      );
    }
  }, [customerFlow?.associateCustomer]);

  useEffect(() => {
    if (authorizationAsyncInfo?.payMean?.isError) {
      const getErrorCode = () => authorizationAsyncInfo?.payMean?.error?.errorCode?.toString();
      const foundedError = errors?.find((el) => el?.errorCode === getErrorCode());
      const ctcConfig = getCtcConfig(foundedError, authorizationAsyncInfo?.payMean?.error);

      checkoutErrors?.showAndTrackError?.(
        {
          ...genericError,
          genericerror: {
            ...genericError.genericerror,
            title: 'Ops!',
            description:
              'Si è verificato un errore durante il recupero dei pagamenti. Riprovare in seguito',
          },
        },
        handleClose,
        { ...authorizationAsyncInfo?.payMean?.error, isBlocking: true },
        ctcConfig?.title,
        ctcConfig?.message,
        undefined,
        '',
        ctcConfig?.secondActionEvent,
        ctcConfig?.secondActionText,
        ctcConfig?.isButtonSticky
      );
    }
  }, [authorizationAsyncInfo?.payMean]);

  useEffect(() => {
    if (deliveryAdvance?.isError) {
      LoggerInstance.log('Error: deliveryadvance_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...deliveryAdvance?.error,
        isBlocking: true,
      });
    }
  }, [deliveryAdvance]);

  useEffect(() => {
    if (replaceBillingAddress?.isError) {
      LoggerInstance.log('Error: replacebilling_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...errorMock('replacebilling', {
          url: 'replacebilling',
          status: '500',
          statusText: '500',
        }),
        isBlocking: true,
      });
    }
  }, [replaceBillingAddress]);

  useEffect(() => {
    if (createBillingAddress?.isError) {
      LoggerInstance.log('Error: createbilling_error');
      checkoutErrors?.showAndTrackError?.(genericError, handleClose, {
        ...errorMock('createbilling', {
          url: 'createbilling',
          status: '500',
          statusText: '500',
        }),
        isBlocking: true,
      });
    }
  }, [createBillingAddress]);

  // endregion

  // if is success delete cart from popup, remove cookie for delete cart on reload
  useEffect(() => {
    if (isSuccessDeleteCart) setItemCookie(COOKIE_CART, '', 0, false, 'privati.vodafone.it');
  }, [isSuccessDeleteCart]);

  useEffect(() => {
    setCheckoutErrors({
      ...checkoutErrors,
      showAndTrackError,
    });
  }, [currentStepKey]);

  return null;
};

